/**
 * templateDesignSystem.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared design system for all ResumeBar templates.
 * Provides: ATS metadata, spacing tokens, typography scale, shared section
 * renderers, and DOM data-ats attribute helpers.
 */

import React from 'react';
import { normalizeTemplateData, safePoint, categorizeSkills } from './templateHelpers';
import EditableField from '../components/EditableField';
import { getRegistryItem } from '../components/editor/registry';

import { TEMPLATE_METADATA, TEMPLATE_CATEGORIES as NEW_CATEGORIES, ALL_TEMPLATE_IDS as NEW_IDS } from './templateMetadata';

// Adapt TEMPLATE_METADATA into TEMPLATE_REGISTRY expected by legacy components
export const TEMPLATE_REGISTRY = {};

Object.keys(TEMPLATE_METADATA).forEach(key => {
  const meta = TEMPLATE_METADATA[key];
  
  // Resolve compatibility variables
  const rAts = meta.ratings?.ats || 95;
  const cTheme = meta.themes?.[0]?.primary || '#7C3AED';
  
  const mapped = {
    ...meta,
    label: meta.name,
    categoryLabel: meta.category === 'ats_professional' ? 'ATS Verified' : 'Standard',
    atsRating: rAts,
    atsLabel: rAts >= 99 ? 'ATS Perfect' : 'ATS Verified',
    atsColor: rAts >= 98 ? '#059669' : '#0369a1',
    bestFor: meta.recommendation?.roles?.[0] || 'Any Role',
    bestForTags: meta.tags || [],
    compatibility: 'Fully parseable by all major ATS systems',
    recommendedFor: meta.recommendation?.roles || [],
    icon: meta.category === 'ats_professional' ? '📄' : '✨',
    description: meta.tags?.join(', ') || '',
    accent: cTheme
  };

  TEMPLATE_REGISTRY[key] = mapped;

  // Backwards compatibility with underscores (e.g. harvard-classic -> harvard_classic)
  const snakeKey = key.replace(/-/g, '_');
  TEMPLATE_REGISTRY[snakeKey] = {
    ...mapped,
    id: snakeKey
  };
});

// Map standard categories and IDs to snake_case for legacy visual queries
export const TEMPLATE_CATEGORIES = NEW_CATEGORIES.map(cat => ({
  ...cat,
  templates: cat.templates.map(t => t.replace(/-/g, '_'))
}));

export const ALL_TEMPLATE_IDS = NEW_IDS.map(id => id.replace(/-/g, '_'));

// ─── Spacing & Typography Design Tokens ───────────────────────────────────────
export const DS = {
  fs: { name: '22px', role: '11px', contact: '9.5px', section: '9.5px', body: '10px', bullet: '10px', small: '9px', label: '8.5px' },
  sp: { pageX: '28px', pageY: '22px', sectionMb: '12px', itemMb: '8px', bulletPl: '12px' },
};

// ─── Shared Section Header ─────────────────────────────────────────────────────
export function SectionHeader({ title, accentColor = '#1a1a2e', style = {} }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, marginBottom: 6, ...style }}>
      <h2 style={{ fontSize: DS.fs.section, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.1px', color: '#111', margin: 0, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>{title}</h2>
      <div style={{ flex: 1, height: 1.5, backgroundColor: accentColor, opacity: 0.6 }} />
    </div>
  );
}

// ─── Bullet List Renderer ──────────────────────────────────────────────────────
export function BulletList({ points = [], accentColor = '#1a1a2e' }) {
  if (!points || points.length === 0) return null;
  return (
    <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none' }}>
      {points.map((pt, i) => {
        const text = safePoint(pt);
        if (!text) return null;
        return (
          <li key={i} style={{ fontSize: DS.fs.bullet, color: '#374151', lineHeight: 1.55, paddingLeft: DS.sp.bulletPl, position: 'relative', marginBottom: 2 }}>
            <span style={{ position: 'absolute', left: 0, color: accentColor, fontWeight: 700, fontSize: '9px', lineHeight: 1.7 }}>▪</span>
            {text}
          </li>
        );
      })}
    </ul>
  );
}

// ─── Skills Renderer ─────────────────────────────────────────────────────────────
// skillCategories: optional user-defined [{ name, skills[] }] from the form editor.
// When present, those are rendered as labelled rows (user intent > auto-grouping).
// Falls back to auto-categorisation from the flat skills array.
export function SkillsBlock({ skills = [], skillCategories = null, accentColor = '#1a1a2e', layout = 'comma' }) {
  if (!skills || skills.length === 0) return null;

  // Resolve which categories to display
  let cats;
  if (Array.isArray(skillCategories) && skillCategories.length > 0) {
    // Use user-defined categories; filter out empty ones
    cats = skillCategories
      .filter(c => Array.isArray(c.skills) && c.skills.length > 0)
      .map(c => ({ label: c.name, items: c.skills }));
  } else {
    cats = categorizeSkills(skills);
  }

  if (layout === 'tags') {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 6px' }}>
        {skills.map((s, i) => (<span key={i} style={{ fontSize: DS.fs.small, fontWeight: 500, color: '#374151', background: '#F3F4F6', border: `1px solid ${accentColor}30`, borderRadius: 4, padding: '2px 8px' }}>{s}</span>))}
      </div>
    );
  }
  if (cats.length > 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {cats.map((cat, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <span style={{ fontSize: DS.fs.label, fontWeight: 700, color: '#111', minWidth: 90, flexShrink: 0, paddingTop: 1 }}>{cat.label}:</span>
            <span style={{ fontSize: DS.fs.body, color: '#374151', lineHeight: 1.5 }}>{cat.items.join(', ')}</span>
          </div>
        ))}
      </div>
    );
  }
  // Single category or no categorisation — render as dot-separated list
  const displaySkills = cats.length === 1 ? cats[0].items : skills;
  return <p style={{ fontSize: DS.fs.body, color: '#374151', margin: 0, lineHeight: 1.55 }}>{displaySkills.join(' · ')}</p>;
}

// ─── Contact Row ───────────────────────────────────────────────────────────────
export function ContactRow({ data, accentColor = '#1a1a2e', editMode = false, onFieldUpdate = () => {} }) {
  const { city, email, phone, links } = data;
  return (
    <p style={{ fontSize: DS.fs.contact, color: '#6B7280', margin: '4px 0 0', lineHeight: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 6px' }}>
      <EditableField id="city" editMode={editMode} value={city || ''} onSave={v => onFieldUpdate('city', v)} placeholder="City, Country" />
      {(city || editMode) && <span style={{ opacity: 0.5 }}>|</span>}
      
      <EditableField id="email" editMode={editMode} value={email || ''} onSave={v => onFieldUpdate('email', v)} placeholder="email@address.com" />
      {(email || editMode) && <span style={{ opacity: 0.5 }}>|</span>}
      
      <EditableField id="phone" editMode={editMode} value={phone || ''} onSave={v => onFieldUpdate('phone', v)} placeholder="+1-234-567-890" />
      {(phone || editMode) && (links?.linkedin || links?.github || editMode) && <span style={{ opacity: 0.5 }}>|</span>}
      
      <EditableField id="linkedin" editMode={editMode} value={links?.linkedin || ''} onSave={v => onFieldUpdate('links.linkedin', v)} placeholder="linkedin.com/in/username" />
      {(links?.linkedin || editMode) && (links?.github || editMode) && <span style={{ opacity: 0.5 }}>|</span>}
      
      <EditableField id="github" editMode={editMode} value={links?.github || ''} onSave={v => onFieldUpdate('links.github', v)} placeholder="github.com/username" />
    </p>
  );
}

// ─── Empty State ───────────────────────────────────────────────────────────────
export function EmptyState() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '60px 32px', textAlign: 'center' }}>
      <h3 style={{ color: '#94a3b8', fontWeight: 600, fontSize: 14, marginBottom: 8 }}>No resume data yet</h3>
      <p style={{ color: '#cbd5e1', fontSize: 12 }}>Enter your details in chat to generate your resume.</p>
    </div>
  );
}

// ─── Re-export helpers for templates ──────────────────────────────────────────
export { normalizeTemplateData, safePoint };

// ─── Dynamic Fallback Section Renderer ─────────────────────────────────────────

export function renderDynamicSection({
  sectionId,
  resumeData,
  accentColor = '#111827',
  editMode = false,
  onFieldUpdate = () => {},
  fs = (base) => base,
}) {
  const section = getRegistryItem(sectionId);
  if (!section) return null;

  const list = resumeData[sectionId];
  if (!list || (Array.isArray(list) && list.length === 0)) return null;

  return (
    <section data-ats={sectionId} key={sectionId} style={{ marginBottom: 10 }}>
      {/* Title */}
      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <h2 style={{
          fontSize: fs('10px'), fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '2px', color: accentColor, margin: 0,
          paddingBottom: 4, borderBottom: `1.5px solid ${accentColor}`,
          fontFamily: 'inherit',
        }}>{section.title}</h2>
      </div>

      {/* Render list or single field */}
      {Array.isArray(list) ? (
        list.map((item, idx) => {
          if (section.type === 'timeline') {
            const mainField = section.fields?.[0]?.name || 'role';
            const subField = section.fields?.[1]?.name || 'company';
            const durationField = section.fields?.[2]?.name || 'duration';
            
            const mainLabel = item[mainField] || '';
            const subLabel = item[subField] || '';
            const duration = item[durationField] || '';
            const points = item.points || item.responsibilities || [];
            
            return (
              <div key={idx} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div>
                    <EditableField
                      editMode={editMode}
                      value={mainLabel}
                      onSave={v => onFieldUpdate(`${sectionId}.${idx}.${mainField}`, v)}
                      tag="h3"
                      style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }}
                    />
                    {subLabel && <span style={{ color: '#6B7280', fontSize: fs('10px') }}>{' | '}{subLabel}</span>}
                  </div>
                  <EditableField
                    editMode={editMode}
                    value={duration}
                    onSave={v => onFieldUpdate(`${sectionId}.${idx}.${durationField}`, v)}
                    tag="span"
                    style={{ fontSize: fs('9px'), color: '#9CA3AF', whiteSpace: 'nowrap', marginLeft: 8, fontWeight: 500 }}
                  />
                </div>
                {points.length > 0 && <BulletList points={points} accentColor={accentColor} />}
              </div>
            );
          } else {
            const mainField = section.fields?.[0]?.name || 'name';
            const secondField = section.fields?.[1]?.name || '';
            const thirdField = section.fields?.[2]?.name || '';

            const mainVal = item[mainField] || '';
            const secondVal = secondField ? item[secondField] : '';
            const thirdVal = thirdField ? item[thirdField] : '';

            return (
              <div key={idx} style={{ fontSize: fs('10px'), color: '#374151', lineHeight: 1.6, marginBottom: 2 }}>
                <span style={{ color: accentColor, fontWeight: 700, marginRight: 6 }}>▪</span>
                <EditableField
                  editMode={editMode}
                  value={mainVal}
                  onSave={v => onFieldUpdate(`${sectionId}.${idx}.${mainField}`, v)}
                  tag="span"
                  style={{ fontWeight: 600, color: '#111' }}
                />
                {secondVal && (
                  <span style={{ color: '#6B7280' }}>
                    {' | '}
                    <EditableField
                      editMode={editMode}
                      value={secondVal}
                      onSave={v => onFieldUpdate(`${sectionId}.${idx}.${secondField}`, v)}
                      tag="span"
                    />
                  </span>
                )}
                {thirdVal && (
                  <span style={{ fontSize: fs('9px'), color: '#9CA3AF', marginLeft: 6 }}>
                    {'('}
                    <EditableField
                      editMode={editMode}
                      value={thirdVal}
                      onSave={v => onFieldUpdate(`${sectionId}.${idx}.${thirdField}`, v)}
                      tag="span"
                    />
                    {')'}
                  </span>
                )}
              </div>
            );
          }
        })
      ) : (
        <EditableField
          editMode={editMode}
          value={String(list)}
          onSave={v => onFieldUpdate(sectionId, v)}
          tag="p"
          multiline
          style={{ fontSize: fs('10px'), color: '#374151', lineHeight: 1.7, margin: 0, textAlign: 'justify' }}
        />
      )}
    </section>
  );
}

